import { IconExternalLink } from '@tabler/icons-react';
import { useState } from 'react';
import {
    NSModal,
    NSButton,
    NSMultiSelect,
} from 'bricks';
import { useQueryClient } from '@tanstack/react-query';
import { OptionType } from 'bricks/NSSelectWithCheckboxes/NSSelectWithCheckboxes';
import { Link } from 'react-router-dom';
import { URLS } from 'services/urls';
import useToast from 'hooks/useToast';
import { useGetOrganizationContacts } from 'views/Deals/hooks/useGetOrganizationContacts';
import { IContact } from '../types';
import { CRM_ENTITY_TYPE } from '../constants';
import useUpsertContactAssociations from './hooks/useUpsertContactAssociations';
import getCRMDealContactsQueryOptions from './hooks/useGetCRMDealContacts';

interface IUpsertProjectsModalProps {
    isOpen: boolean;
    toggle: () => void;
    organizationId: string;
    dealId: number;
    existingAssociations: IContact[];
}

const UpsertContactsModal = ({
    isOpen, toggle, organizationId, dealId, existingAssociations,
}: IUpsertProjectsModalProps) => {
    const queryClient = useQueryClient();
    const { showSuccess, showError } = useToast();

    const {
        data: orgContacts,
        isLoading: isLoadingOptions,
    } = useGetOrganizationContacts();

    const [selected, setSelected] = useState<OptionType[]>(existingAssociations?.map(e => ({
        label: e.name,
        value: e.correlationId,
    })) || [] as OptionType[]);

    const {
        mutate: upsertAssociations,
        isPending: isSaving,
    } = useUpsertContactAssociations({
        onSuccess: () => {
            showSuccess('Contacts associated successfully');
            queryClient.invalidateQueries(getCRMDealContactsQueryOptions(organizationId, dealId));
            toggle();
        },
        onError: error => {
            showError(`There was an error associating contacts. Please try again. ${error?.message}`);
        },
    });

    const handleCreate = () => upsertAssociations({
        organizationId,
        associationType: CRM_ENTITY_TYPE.DEAL,
        associationId: dealId,
        contactCorrelationIds: selected.map(e => e.value),
    });

    const options = orgContacts?.map(e => ({
        label: e.name,
        value: e.correlationId,
    })) || [];

    return (
        <NSModal
            isOpen={isOpen}
            toggle={toggle}
            modalHeaderText="Associate contacts"
            modalHeaderClassName="bg-primary text-white"
            customFooter={(
                <div>
                    <NSButton
                        text="Cancel"
                        className="mr-2"
                        outline={false}
                        callback={toggle}
                    />
                    <Link
                        to={URLS.PROJECTS_APP.CONTACTS}
                        target="_blank"
                        rel="noreferrer"
                        className="cursor--pointer"
                    >
                        <NSButton
                            text="Create new contact"
                            color="secondary"
                            outline={false}
                            className="heap-deal-activity-create-new-contact"
                            icon={IconExternalLink}
                        />
                    </Link>
                    <NSButton
                        className="ml-2 heap-deal-activity-add-contact"
                        text="Save"
                        color="primary"
                        outline={false}
                        callback={handleCreate}
                        isLoading={isSaving}
                    />
                </div>
            )}
        >
            <NSMultiSelect
                options={options}
                selectName="add-contacts"
                label="Contacts"
                value={selected}
                onChange={setSelected}
                isClearable={false}
                componentClassName="mt-1"
                isLoading={isLoadingOptions}
            />

        </NSModal>
    );
};

export default UpsertContactsModal;
